// src/oura/newscan/useScanningSites.ts
//
// Which sites have a scan running, for the Scanning tag on the businesses
// list and the business page. The tag is the lasting confirmation that a
// scan started; the snackbar on landing is only the "you left" channel.
//
//   live       the scan jobs (useScanJobs): the Start step rehydrates them,
//              so a started run is a queued or running job for its domain.
//   fixtures   nothing real starts, so the sample server's started drafts
//              (last 30 minutes) stand in, read again on every navigation.
//
// Does nothing with the New scan switch off, so those screens stay as today.

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useScanJobs } from "@oura/aeo";
import { listStartedScanDrafts } from "@service/aeo/scanDraft";
import type { StartedScanDraft } from "@service/aeo/scanDraftTypes";
import { SCAN_DRAFT_UI_ON } from "./flags";
import { siteKey } from "./domain";
import { FIRST_CHECK_MINUTES } from "@oura/onboarding/derive";

export interface ScanningSites {
    /** "Scanning · about 9 minutes", or "Scanning" once the estimate has run out; null when nothing runs for this site. */
    labelFor(domain: string | null | undefined, businessId?: string | null): string | null;
    /** New businesses the sample server started a scan for (fixtures only), newest first. */
    started: StartedScanDraft[];
}

const MINUTE = 60 * 1000;
/** Fixtures only (live lists nothing here): how often a started scan is read again. */
const RECHECK_MS = 5000;

function label(remainingMs: number | null): string {
    if (remainingMs === null || remainingMs <= 0) return "Scanning";
    const n = Math.max(1, Math.ceil(remainingMs / MINUTE));
    return `Scanning · about ${n} ${n === 1 ? "minute" : "minutes"}`;
}

export function useScanningSites(): ScanningSites {
    const jobs = useScanJobs();
    const location = useLocation();
    const [started, setStarted] = useState<StartedScanDraft[]>([]);
    const [now, setNow] = useState(() => Date.now());

    // Read again on every navigation (landing here from the Start step is one), and
    // every few seconds while one runs, so a finished first check drops its tag in place.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        if (!SCAN_DRAFT_UI_ON) return;
        let cancelled = false;
        void listStartedScanDrafts().then((res) => {
            if (!cancelled && res.ok) setStarted(res.data);
        });
        return () => {
            cancelled = true;
        };
    }, [location.key, tick]);
    const anyStarted = started.length > 0;
    useEffect(() => {
        if (!anyStarted) return;
        const t = window.setInterval(() => setTick((n) => n + 1), RECHECK_MS);
        return () => window.clearInterval(t);
    }, [anyStarted]);

    const running = useMemo(() => jobs.filter((j) => j.status === "queued" || j.status === "running"), [jobs]);
    const any = SCAN_DRAFT_UI_ON && (running.length > 0 || started.length > 0);

    // The minutes count down while something runs.
    useEffect(() => {
        if (!any) return;
        setNow(Date.now());
        const t = window.setInterval(() => setNow(Date.now()), MINUTE);
        return () => window.clearInterval(t);
    }, [any]);

    return useMemo<ScanningSites>(() => {
        const byJob = new Map<string, string>();
        running.forEach((j) => {
            const key = siteKey(j.domain || "");
            if (!key || byJob.has(key)) return;
            const s = j.eta?.remaining_seconds;
            byJob.set(key, label(typeof s === "number" ? s * 1000 : null));
        });
        // One duration everywhere (JOURNEY-SPEC §2.5): FIRST_CHECK_MINUTES, not the sample server's estimate.
        const left = (d: StartedScanDraft) => Date.parse(d.startedAt) + FIRST_CHECK_MINUTES * MINUTE - now;
        return {
            started: SCAN_DRAFT_UI_ON ? started : [],
            labelFor(domain, businessId) {
                if (!SCAN_DRAFT_UI_ON) return null;
                const key = domain ? siteKey(domain) : "";
                if (key && byJob.has(key)) return byJob.get(key) ?? null;
                const hit = started.find((d) => (key && siteKey(d.domain) === key) || (businessId && d.businessId === businessId));
                return hit ? label(left(hit)) : null;
            },
        };
    }, [running, started, now]);
}
